// Weergaveregels voor PDF's en schermen: de werkposten en meerwerken zoals de onderaannemer ze
// betaald krijgt — met de crepi-ladder per schijf, zonder de regels die de ladder overneemt en
// zonder posten die (nog) niet betaald worden wegens overlap.
import { berekenGevel, type GevelConfig } from './gevel.js';
import { berekenSchijven, round2, CREPI_DEEL_LABEL } from './schijven.js';
import { lineSubtotaal, meerwerkTeltMee, werkpostTeltMee } from './matching.js';
import type { MatchedLine, MeerwerkLine } from './types.js';

export interface WeergaveRij {
  naam: string;
  hoeveelheid: number;
  eenheid: string;
  /** null = varieert of schijven (zie toelichting). */
  prijs: number | null;
  subtotaal: number;
  /** Bv. "10 × 80,00 + 1 × 60,00" bij schijven. */
  toelichting?: string;
}

function fmt(n: number): string {
  return n.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function weergaveRegels(
  matchedLines: MatchedLine[],
  meerwerkLines: MeerwerkLine[],
  gevel: GevelConfig | null | undefined,
): { werkposten: WeergaveRij[]; meerwerk: WeergaveRij[]; extraOffertes: WeergaveRij[] } {
  // Nog niet bevestigde extra offertes en eenheidsvragen staan niet op de PDF (SPEC_workflow 4, calculatie 8.1).
  matchedLines = matchedLines.filter(werkpostTeltMee);
  meerwerkLines = meerwerkLines.filter(meerwerkTeltMee);
  const res = gevel ? berekenGevel(matchedLines, meerwerkLines, gevel) : null;
  const actief = res?.actief ? res : null;
  const vervangen = new Set(actief?.vervangenLijnIds ?? []);
  const vervangenMw = new Set(actief?.vervangenMeerwerkIds ?? []);
  const v = gevel ? ` (variant ${gevel.variant})` : '';

  const werkposten: WeergaveRij[] = matchedLines
    .filter((l) => !vervangen.has(l.id) && (!l.overlap || l.overlap.status === 'bevestigd'))
    .map((l) => ({
      naam: l.naamArbeid,
      hoeveelheid: l.hoeveelheid,
      eenheid: l.eenheid,
      prijs: l.schijven ? null : l.prijs,
      subtotaal: lineSubtotaal(l),
      toelichting: l.schijven
        ? berekenSchijven(Math.max(0, l.hoeveelheid || 0), l.schijven).regels.map((r) => `${fmt(r.hoeveelheid)} × ${fmt(r.prijs)}`).join(' + ')
        : undefined,
    }));
  const rijVan = (m: MeerwerkLine): WeergaveRij => ({ naam: m.pdfNaam, hoeveelheid: m.hoeveelheid, eenheid: m.eenheid, prijs: m.prijs, subtotaal: m.subtotaal });
  const meerwerk: WeergaveRij[] = meerwerkLines.filter((m) => !vervangenMw.has(m.id) && !m.extraOfferte).map(rijVan);
  // Extra werken uit offertes na de opstartfiche, onder een eigen subtitel (SPEC_workflow 4).
  const extraOffertes: WeergaveRij[] = meerwerkLines
    .filter((m) => !vervangenMw.has(m.id) && m.extraOfferte)
    .map((m) => ({ ...rijVan(m), toelichting: `offerte ${m.extraOfferte!.nummer}${m.extraOfferte!.projectCode ? `, ${m.extraOfferte!.projectCode}` : ''}` }));

  if (actief && gevel) {
    for (const r of actief.werkposten.regels) {
      werkposten.push({ naam: `${CREPI_DEEL_LABEL[r.deel]}${v}, schijf ${r.schijf}`, hoeveelheid: r.hoeveelheid, eenheid: 'M2', prijs: r.prijs, subtotaal: r.bedrag });
    }
    const ss = actief.steenstrips;
    if (ss.m2ZonderVoeg > 0) werkposten.push({ naam: `Steenstrips 2 cm zonder voeg${v}`, hoeveelheid: ss.m2ZonderVoeg, eenheid: 'M2', prijs: gevel.steenstrips.zonderVoeg, subtotaal: round2(ss.m2ZonderVoeg * gevel.steenstrips.zonderVoeg) });
    if (ss.m2MetVoeg > 0) werkposten.push({ naam: `Steenstrips 2 cm met voeg${v}`, hoeveelheid: ss.m2MetVoeg, eenheid: 'M2', prijs: gevel.steenstrips.metVoeg, subtotaal: round2(ss.m2MetVoeg * gevel.steenstrips.metVoeg) });
    if (actief.crepiMeerwerk) {
      meerwerk.push({
        naam: `Extra crepi via de schijvenladder${v}`,
        hoeveelheid: round2(actief.metMeerwerk.m2Crepi - actief.werkposten.m2Crepi),
        eenheid: 'M2',
        prijs: null,
        subtotaal: actief.crepiMeerwerk,
        toelichting: 'de extra m2 schuiven de schijven mee',
      });
    }
    if (ss.bedragMeerwerk) {
      meerwerk.push({ naam: `Extra steenstrips 2 cm${v}`, hoeveelheid: ss.m2MeerwerkZonderVoeg + ss.m2MeerwerkMetVoeg, eenheid: 'M2', prijs: null, subtotaal: ss.bedragMeerwerk });
    }
  }
  return { werkposten, meerwerk, extraOffertes };
}
